import {
  Badge, Button, Card, DateInput, ErrorState, Field, Input, LARGEST_PAGE, Notice, PageHeader, Roles, Row, Select,
  Stack, invalidDateIn, useApi, useI18n, useRoleLabel,
  type ClassRole, type StaffResponse,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { OFFERED_ROLES } from '../staffRoles';


const CLASS_ROLES: ClassRole[] = ['Lead', 'Assistant', 'Substitute'];

/**
 * One staff member's name, role, job title and classes (FR-ADM-14, FR-ADM-15), behind `staff.update`.
 *
 * Classes are added with an optional date range, which is how cover for an absent colleague is given without touching
 * anyone's permanent class. Ending one removes the latest assignment to that class; one a closed term holds is never
 * removed (Q34), and the API says so in its own words.
 */
export function StaffEditor({ member, onClose }: { member: StaffResponse; onClose: () => void }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t, formatDate } = useI18n();
  const roleLabel = useRoleLabel();

  const [fullName, setFullName] = useState(member.fullName ?? '');
  const [jobTitle, setJobTitle] = useState(member.jobTitle ?? '');
  const [roleKey, setRoleKey] = useState(member.roleKey ?? Roles.tenant.teacher);

  const [classId, setClassId] = useState<number | ''>('');
  const [roleInClass, setRoleInClass] = useState<ClassRole>('Lead');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['staff'] });

  const staff = useQuery({ queryKey: ['staff'], queryFn: () => api.staff.list() });
  // The row as the list has it now, so an assignment added here shows at once.
  const current = staff.data?.find((row) => row.membershipId === member.membershipId) ?? member;

  const classes = useQuery({
    queryKey: ['classes', { status: 'Active', pageSize: LARGEST_PAGE }],
    queryFn: () => api.classes.list({ status: 'Active', pageSize: LARGEST_PAGE }),
  });

  const save = useMutation({
    mutationFn: () => api.staff.update(member.membershipId, {
      fullName: fullName.trim() || null,
      jobTitle: jobTitle.trim(),
      roleKey: roleKey === member.roleKey ? null : roleKey,
    }),
    onSuccess: refresh,
  });

  const assign = useMutation({
    mutationFn: () => api.staff.assignClass(member.membershipId, {
      classId: Number(classId), roleInClass, startDate: startDate || null, endDate: endDate || null,
    }),
    onSuccess: async () => {
      setClassId('');
      setStartDate('');
      setEndDate('');
      await refresh();
    },
  });

  const unassign = useMutation({
    mutationFn: (id: number) => api.staff.unassignClass(member.membershipId, id),
    onSuccess: refresh,
  });

  const submitAssignment = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (invalidDateIn(event.currentTarget) || classId === '') return;
    assign.mutate();
  };

  // The member's own role stays offered even where it is not among the ones a new invitation offers.
  const roles = OFFERED_ROLES.includes(member.roleKey ?? '') || !member.roleKey ? OFFERED_ROLES : [member.roleKey, ...OFFERED_ROLES];

  return (
    <Stack>
      <PageHeader
        title={t('staff.editOf', { name: member.fullName ?? '' })}
        actions={<Button variant="ghost" onClick={onClose}>{t('action.back')}</Button>}
      />

      <Card title={t('staff.details')}>
        <Stack>
          <Field label={t('staff.name')}>
            {(props) => <Input {...props} value={fullName} onChange={(event) => setFullName(event.target.value)} />}
          </Field>
          <Field label={t('staff.jobTitle')}>
            {(props) => <Input {...props} value={jobTitle} onChange={(event) => setJobTitle(event.target.value)} />}
          </Field>
          <Field label={t('staff.role')} hint={t('staff.role.hint')}>
            {(props) => (
              <Select {...props} value={roleKey} onChange={(event) => setRoleKey(event.target.value)}>
                {roles.map((role) => <option key={role} value={role}>{roleLabel(role) ?? role}</option>)}
              </Select>
            )}
          </Field>
          {save.isError && <ErrorState error={save.error} />}
          {save.isSuccess && <Notice tone="success">{t('staff.saved')}</Notice>}
          <Row>
            <Button busy={save.isPending} busyLabel={t('action.saving')} onClick={() => save.mutate()}>{t('action.save')}</Button>
          </Row>
        </Stack>
      </Card>

      <Card title={t('staff.classes')} hint={t('staff.classes.hint')}>
        <Stack>
          {current.classes.length === 0 && <p className="dg-card__hint">{t('staff.classes.none')}</p>}
          {current.classes.map((assignment, index) => (
            <Row key={`${assignment.classId}-${index}`} between>
              <span className="dg-stack dg-stack--tight">
                <strong>{assignment.className}</strong>
                <span className="dg-card__hint">
                  {t(`staff.roleInClass.${assignment.roleInClass}`)}
                  {assignment.startDate || assignment.endDate
                    ? ` · ${t('staff.assignment.span', {
                        from: assignment.startDate ? formatDate(assignment.startDate) : '—',
                        to: assignment.endDate ? formatDate(assignment.endDate) : '—',
                      })}`
                    : ''}
                </span>
              </span>
              <Button
                variant="ghost"
                busy={unassign.isPending && unassign.variables === assignment.classId}
                aria-label={t('staff.unassignFrom', { name: assignment.className })}
                onClick={() => unassign.mutate(assignment.classId)}
              >
                {t('staff.unassign')}
              </Button>
            </Row>
          ))}
          {unassign.isError && <ErrorState error={unassign.error} />}

          <form noValidate onSubmit={submitAssignment}>
            <Stack>
              <Row className="dg-row--wrap">
                <Field label={t('class.one')} required>
                  {(props) => (
                    <Select {...props} value={classId}
                      onChange={(event) => setClassId(event.target.value === '' ? '' : Number(event.target.value))}>
                      <option value="">—</option>
                      {(classes.data?.items ?? []).map((klass) => <option key={klass.id} value={klass.id}>{klass.name}</option>)}
                    </Select>
                  )}
                </Field>
                <Field label={t('staff.roleInClass')}>
                  {(props) => (
                    <Select {...props} value={roleInClass} onChange={(event) => setRoleInClass(event.target.value as ClassRole)}>
                      {CLASS_ROLES.map((role) => <option key={role} value={role}>{t(`staff.roleInClass.${role}`)}</option>)}
                    </Select>
                  )}
                </Field>
              </Row>
              <Row className="dg-row--wrap">
                <Field label={t('staff.assign.from')}>
                  {(props) => <DateInput {...props} value={startDate} onChange={setStartDate} />}
                </Field>
                <Field label={t('staff.assign.to')} hint={t('staff.assign.coverHint')}>
                  {(props) => <DateInput {...props} value={endDate} min={startDate || undefined} onChange={setEndDate} />}
                </Field>
              </Row>
              {assign.isError && <ErrorState error={assign.error} />}
              <Row>
                <Button type="submit" variant="secondary" disabled={classId === ''} busy={assign.isPending}>
                  {t('staff.assign')}
                </Button>
              </Row>
            </Stack>
          </form>
        </Stack>
      </Card>

      {current.status !== 'Active' && <Badge tone="neutral">{t(`status.${current.status}`)}</Badge>}
    </Stack>
  );
}
